import SearchBar from "../../../../Components/Inputs/SearchBar";
import { useTranslation } from "react-i18next";
import GreenBtn from "../../../../Components/Buttons/GreenBtn";
import TableCont from "../../../../Components/Tables/TableCont";
import { backlogTrailerHeaders } from "../../../../Components/Tables/Headers";
import { useWindowWidth } from "../../../../Hooks/useWindowWidth";
import { GetVehicleCondition } from "../../../../Components/Tables/Vehicles/GetVehicleCondition";
import { formatReadableDateTime } from "../../../../Helper/FormatDatetimes";
import { TableCell } from "../../../../Components/Tables/Components/TableCell";
import DebouncedInput from "../../../../Components/Inputs/DebouncedInput";
import { useEffect, useReducer, useState } from "react";
import { initialState, reducerFunction } from "../Reducer";
import { useTransformData } from "../../../../Hooks/useTransformData";
import instance from "../../../../Interceptor/api_instance";
import { TablePlaceHolder } from "../../../../Components/LoadingAnimations/LoadingAnimations";
import { TableNavigation } from "../../../../Components/Tables/Vehicles/TableNavigation";
import { RowClickModal } from "../../../../Components/Modals/RowClickModal";
import { EditBacklogTrailer } from "../Trucks/EditBacklogRow";
import TableImgModal from "../../../../Components/Modals/TableImgModal";
import { SpinnerOverlay } from "../../../../Components/LoadingAnimations/SpinnerOverlay";
import { createCsv } from "../../../../Components/Utility/ExportToFile";

const TrailersBacklogTable = () => {
  const { t } = useTranslation();
  const headers = backlogTrailerHeaders(t);

  const { TransformEventData } = useTransformData();
  const [state, dispatch] = useReducer(reducerFunction, initialState);
  const [filters, setFilters] = useState({
    location: null,
    trailer_number: null,
    type: null,
    customer: null,
    startDate: null,
    endDate: null,
  });

  const handleFilter = (name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Check if user is on mobile (phone)
  const width = useWindowWidth();
  const isMobile = width <= 500;

  const handlePrint = async () => {
    try {
      dispatch({ type: "LOAD_PAGE", payload: true });

      // Use current filters applied
      const queryString = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null) {
          // Exclude null values
          queryString.append(key, value);
        }
      });
      const url = `assets/backlog/trailers?${queryString.toString()}&paginate=false`;

      const data = await instance.get(url);

      // Parse data and use key names which will be used as headers
      if (!data || data.length === 0) return;

      const parsedData = data.data.map((row) => ({
        [t("yard-tables.table-columns.location")]: row.location.loc_name,
        [t("yard-tables.table-columns.trailer")]: row.trailer.trailer_number,
        [t("yard-tables.table-columns.eventType")]: row.check_in_dt
          ? t("yard-tables.table-columns.in")
          : row.check_out_dt
          ? t("yard-tables.table-columns.out")
          : "N/A",
        [t("yard-tables.table-columns.guard")]: row.employee
          ? row.employee.full_name
          : row.user.full_name,
        [t("yard-tables.table-columns.truck")]: row.truck.unit,
        [t("yard-tables.table-columns.driver")]: row.visitor.full_name,
        [t("yard-tables.table-columns.event-time")]: formatReadableDateTime(
          row.check_in_dt || row.check_out_dt
        ),
      }));
      createCsv(parsedData, `trailers_backlog_${new Date().toISOString()}`);
    } catch (err) {
      console.log(err);
    } finally {
      dispatch({ type: "LOAD_PAGE", payload: false });
    }
  };

  useEffect(() => {
    const queryString = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null) {
        // Exclude null values
        queryString.append(key, value);
      }
    });
    const url = `assets/backlog/trailers?${queryString.toString()}`;
    dispatch({ type: "SET_CURRENT_PAGE", page: url, pageNum: 1 });
  }, [filters]);

  const fetchData = async () => {
    try {
      dispatch({ type: "FETCH_DATA" });
      const response = await instance.get(state.currentPage);
      if (response.status === 200) {
        dispatch({
          type: "STORE_DATA",
          payload: TransformEventData(response.data.results),
          next: response.data.next,
          previous: response.data.previous,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [state.currentPage]);

  return (
    <>
      {state.pageLoading && <SpinnerOverlay />}
      {state.selectedRow && (
        <RowClickModal
          setEditRow={() => dispatch({ type: "EDIT_ROW" })}
          setDocuments={() => dispatch({ type: "OPEN_DOCS" })}
          row={state.selectedRow}
          type={"trailers"}
          close={() => dispatch({ type: "CLOSE_SELECTED_ROW" })}
          english={t}
        />
      )}
      {state.editRow && (
        <EditBacklogTrailer
          row={state.selectedRow}
          close={() => dispatch({ type: "CLOSE_EDIT_ROW" })}
        />
      )}
      {state.docsModal && (
        <TableImgModal
          info={state.selectedRow.trailer.unit}
          trailer={state.selectedRow}
          english={t}
          close={() => dispatch({ type: "CLOSE_DOCS" })}
        />
      )}
      <div className="search-filters-cont">
        <div id="filter-form">
          <div className="search-filters" style={{ alignItems: "center" }}>
            <label>
              {t("general.startDate")}:{" "}
              <input
                type="date"
                className="text-input-sm"
                value={filters.startDate}
                onChange={(e) => handleFilter("startDate", e.target.value)}
              />
            </label>
            <label>
              {t("general.endDate")}:{" "}
              <input
                type="date"
                value={filters.endDate}
                className="text-input-sm"
                onChange={(e) => handleFilter("endDate", e.target.value)}
              />
            </label>
            <select
              className="text-input-sm"
              value={filters.location}
              onChange={(e) => handleFilter("location", e.target.value)}
            >
              <option value="">
                {t("yard-tables.table-columns.location")}
              </option>
              <option value="Tijuana Yard">Tijuana</option>
              <option value="Queretaro Yard">Queretaro</option>
              <option value="San Diego Yard">San Diego</option>
            </select>
            <select
              className="text-input-sm"
              value={filters.type}
              onChange={(e) => handleFilter("type", e.target.value)}
            >
              <option value="">
                {t("yard-tables.table-columns.event-type")}
              </option>
              <option value="in">{t("yard-tables.table-columns.in")}</option>
              <option value="out">{t("yard-tables.table-columns.out")}</option>
            </select>
            <DebouncedInput
              state={filters.trailer_number}
              setState={handleFilter}
              label={"trailer"}
              name={"trailer_number"}
            />
            <DebouncedInput
              state={filters.customer}
              setState={handleFilter}
              label={"customer"}
              name={"customer"}
            />
            <SearchBar />
            <GreenBtn text={t("buttons.print")} onClick={handlePrint} />
          </div>
          <TableNavigation
            pageNum={state.currentPageNum}
            state={state}
            dispatch={dispatch}
          />
        </div>
      </div>

      {state.isLoading ? (
        <TablePlaceHolder />
      ) : (
        <TableCont>
          <thead>
            <tr className="table-header">
              {headers.map((header, index) => (
                <th scope="col" key={`${header}-${index}`}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {state.data.map((row, index) => (
              <tr
                key={index}
                className="data-rows row-hover"
                onClick={() => {
                  dispatch({ type: "SET_SELECTED_ROW", selectedRow: row });
                }}
              >
                <TableCell
                  index={index}
                  column={"location"}
                  isMobile={isMobile}
                  value={row.location.loc_name}
                />
                <TableCell
                  index={index}
                  column={"trailer"}
                  isMobile={isMobile}
                  value={row.trailer.trailer_number}
                />
                <TableCell
                  index={index}
                  column={"type"}
                  isMobile={isMobile}
                  value={(() => {
                    const type = row.check_in_dt
                      ? t("yard-tables.table-columns.in")
                      : row.check_out_dt
                      ? t("yard-tables.table-columns.out")
                      : "N/A";
                    return type;
                  })()}
                />
                <TableCell
                  index={index}
                  column={"guard"}
                  isMobile={isMobile}
                  value={
                    row.employee ? row.employee.full_name : row.user.fullName
                  }
                />
                <TableCell
                  index={index}
                  column={"truck"}
                  isMobile={isMobile}
                  value={row.truck.unit}
                />
                <TableCell
                  index={index}
                  column={"driver"}
                  isMobile={isMobile}
                  value={row.visitor.driverName}
                />
                <TableCell
                  index={index}
                  column={"reservation"}
                  isMobile={isMobile}
                  value={row.trailer.fullName || "N/A"}
                />
                <TableCell
                  index={index}
                  column={"content"}
                  isMobile={isMobile}
                  value={row.trailer.isLoaded}
                />
                <TableCell
                  index={index}
                  column={"customer"}
                  isMobile={isMobile}
                  value={row.trailer.customer}
                />
                <td className="gray" key={`${index}-condition`}>
                  {isMobile && (
                    <strong>{t("yard-tables.table-columns.condition")}</strong>
                  )}
                  <GetVehicleCondition
                    condition={row.trailer.trailerCondition}
                  />
                </td>
                <TableCell
                  index={index}
                  column={"sealNumber"}
                  isMobile={isMobile}
                  value={row.trailer.sealNumber}
                />
                <TableCell
                  index={index}
                  column={"checkOutTime"}
                  isMobile={isMobile}
                  value={(() => {
                    const eventTime = row.check_in_dt
                      ? row.check_in_dt
                      : row.check_out_dt
                      ? row.check_out_dt
                      : null;
                    return formatReadableDateTime(eventTime);
                  })()}
                />
              </tr>
            ))}
          </tbody>
        </TableCont>
      )}
    </>
  );
};

export default TrailersBacklogTable;
